import { z } from 'zod/v4';
import { ExportNetworkAssetCatalogueSchema } from './export-network-assets.js';

/**
 * Catalogue behind the export dialog's data point picker.
 *
 * Counts describe exactly the companies being exported, which is why the ids
 * travel in a POST body rather than a query string.
 *
 * The counts measure non-empty *storage*; the export's column set measures
 * non-empty *rendering*. A value whose references all fail to resolve is counted
 * here and yields no column — rare, and in the safe direction (never a blank
 * column the picker promised had data).
 */
export const ExportNetworkDataPointsCatalogueRequestSchema = z
  .object({
    ids: z.array(z.string().min(1)).max(500).optional().default([]),
    /** Mirrors the export request, so the audit counts describe the same set. */
    includeOwnCompany: z.boolean().optional().default(false),
  })
  .strip();

export type ExportNetworkDataPointsCatalogueRequest = z.infer<
  typeof ExportNetworkDataPointsCatalogueRequestSchema
>;

export const ExportNetworkDataPointSummarySchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  fieldTypeId: z.string().nullable(),
  sortingId: z.number().nullable(),
  answeredCompaniesCount: z.number().int(),
});

export type ExportNetworkDataPointSummary = z.infer<
  typeof ExportNetworkDataPointSummarySchema
>;

export const ExportNetworkSectionSchema = z.object({
  id: z.string(),
  name: z.string(),
});

export type ExportNetworkSection = z.infer<typeof ExportNetworkSectionSchema>;

export const ExportNetworkDataPointGroupSchema = z.object({
  templateId: z.string(),
  templateName: z.string().nullable(),
  /**
   * The template's dotted impact-area path (`2.1.2`), or null when the template
   * has no impact area. Groups arrive ordered by it, so `About` (1.1) leads and
   * the picker matches the order of the profile tabs.
   */
  sectionId: z.string().nullable(),
  /**
   * The impact areas above this template, outermost first — `[ESG,
   * Environmental]` for a template under `2.1.x`. Empty when the template has
   * no impact area, which is what lets the picker nest it at the top level.
   */
  sectionPath: z.array(ExportNetworkSectionSchema),
  dataPoints: z.array(ExportNetworkDataPointSummarySchema),
});

export type ExportNetworkDataPointGroup = z.infer<
  typeof ExportNetworkDataPointGroupSchema
>;

export const ExportNetworkDataPointsCatalogueResponseSchema = z.object({
  companiesCount: z.number().int(),
  /** Data points at least one exported company has answered. */
  answeredDataPointsCount: z.number().int(),
  /** Every data point in the catalogue, answered or not. */
  totalDataPointsCount: z.number().int(),
  groups: z.array(ExportNetworkDataPointGroupSchema),
  /** Standards the network holds. Required, so the empty response carries it. */
  certifications: ExportNetworkAssetCatalogueSchema,
  /** Audit types the network holds, already access-scoped to the caller. */
  audits: ExportNetworkAssetCatalogueSchema,
  /** Document types the network holds, in the caller's view of the library. */
  documents: ExportNetworkAssetCatalogueSchema,
});

export type ExportNetworkDataPointsCatalogueResponse = z.infer<
  typeof ExportNetworkDataPointsCatalogueResponseSchema
>;

/**
 * The data point half alone.
 *
 * Lets `NetworkDataPointCatalogueService` stay ignorant of assets — it resolves
 * templates and answers, and something above it composes the two asset branches
 * onto the response.
 */
export type ExportNetworkDataPointsCatalogue = Omit<
  ExportNetworkDataPointsCatalogueResponse,
  'certifications' | 'audits' | 'documents'
>;
